import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API, storage } from '../App';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import ImageUploader from '../components/ImageUploader';

const NewListing = ({ currentUserId, appMode, storage }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const editListing = location.state?.editListing;
  const [uploading, setUploading] = useState(false);
  const uploaderRef = useRef(null);
  const [stats, setStats] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(true);
  const [formData, setFormData] = useState({
    title: editListing?.title || '',
    price: editListing?.price || '',
    type: editListing?.type || 'sale',
    propertyType: editListing?.propertyType || 'apartment',
    propertyAddress: editListing?.propertyAddress || '',
    companyName: editListing?.companyName || '',
    workingAddress: editListing?.workingAddress || '',
    contactNumber: editListing?.contactNumber || '',
    whatsappNumber: editListing?.whatsappNumber || '',
    description: editListing?.description || '',
    ratesAndTaxes: editListing?.ratesAndTaxes || '',
    levies: editListing?.levies || '',
    agentProfileFile: null, // Agent profile image
    existingImages: editListing?.images || [],
    existingAgentProfileImage: editListing?.agentProfileImage || '',
    specifications: {
      beds: editListing?.specifications?.beds || '',
      baths: editListing?.specifications?.baths || '',
      lounges: editListing?.specifications?.lounges || '',
      kitchens: editListing?.specifications?.kitchens || '',
      diningRooms: editListing?.specifications?.diningRooms || '',
      propertySize: editListing?.specifications?.propertySize || '',
      floorSize: editListing?.specifications?.floorSize || '',
      ratesTaxes: editListing?.specifications?.ratesTaxes || '',
      hectares: editListing?.specifications?.hectares || ''
    },
    amenities: {
      pool: editListing?.amenities?.pool || editListing?.amenities?.hasPool || false,
      garden: editListing?.amenities?.garden || editListing?.amenities?.hasGarden || false,
      security: editListing?.amenities?.security || false,
      garage: editListing?.amenities?.garage || editListing?.amenities?.hasGarage || false,
      airConditioning: editListing?.amenities?.airConditioning || false,
      petFriendly: editListing?.amenities?.petFriendly || editListing?.amenities?.isPetFriendly || false,
      fibre: editListing?.amenities?.fibre || editListing?.amenities?.hasFiber || false,
      borehole: editListing?.amenities?.borehole || false,
      solarPanels: editListing?.amenities?.solarPanels || false,
      staffAccommodation: editListing?.amenities?.staffAccommodation || false
    },
    customAmenities: editListing?.customAmenities || [] // Custom amenities array
  });
  
  const [customAmenityInput, setCustomAmenityInput] = useState('');
  const [agentProfilePreview, setAgentProfilePreview] = useState(editListing?.agentProfileImage || '');

  if (appMode !== 'Agent' && appMode !== 'Private Owner') {
    navigate('/marketplace');
    return null;
  }

  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const response = await axios.get(`${API}/subscriptions/user/${currentUserId}/stats`);
        setStats(response.data);
        
        // If editing, allow regardless of limit
        if (!editListing && !response.data.canListMore && response.data.subscriptionStatus !== 'none') {
          toast.error('You have reached your property listing limit. Please upgrade your subscription.');
          navigate('/subscription');
        }
      } catch (error) {
        console.error('Error checking subscription:', error);
      } finally {
        setCheckingSubscription(false);
      }
    };

    if (currentUserId) {
      checkSubscription();
    }
  }, [currentUserId, editListing]);

  if (checkingSubscription) {
    return (
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center py-12">
          <i className="fas fa-spinner fa-spin text-4xl text-emerald-600"></i>
          <p className="mt-4 text-gray-600">Checking subscription...</p>
        </div>
      </main>
    );
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSpecChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      specifications: { ...prev.specifications, [field]: value }
    }));
  };

  const handleAmenityChange = (field, checked) => {
    setFormData(prev => ({
      ...prev,
      amenities: { ...prev.amenities, [field]: checked }
    }));
  };

  // Compress an image file via canvas and return a JPEG data URL.
  // maxDim caps both width/height while keeping aspect ratio; quality controls JPEG compression.
  const compressImage = (file, maxDim = 800, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      if (!file || !file.type?.startsWith('image/')) {
        return reject(new Error('Not an image file'));
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          let { width, height } = img;
          if (width > height && width > maxDim) {
            height = Math.round((height * maxDim) / width);
            width = maxDim;
          } else if (height >= width && height > maxDim) {
            width = Math.round((width * maxDim) / height);
            height = maxDim;
          }
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.onerror = reject;
        img.src = e.target.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const uploadLogo = async (file) => {
    if (!file) return '';

    // Check file size (max 5MB before compression)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image file size must be less than 5MB');
      return '';
    }

    try {
      // Compress to keep payload small and reduce storage cost
      return await compressImage(file, 512, 0.82);
    } catch (error) {
      console.error('Error processing logo:', error);
      toast.warning('Image upload failed');
      return '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.price) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (!formData.propertyAddress?.trim()) {
      toast.error('Please enter the property address so buyers can see the location');
      return;
    }

    // Validate land size for Vacant Land and Farm
    if ((formData.propertyType === 'vacant_land' || formData.propertyType === 'farm') && 
        (!formData.specifications.hectares || parseFloat(formData.specifications.hectares) <= 0)) {
      toast.error('Land Size (hectares) is required for Vacant Land and Farm properties');
      return;
    }

    try {
      setUploading(true);
      
      // Upload the original photo files; nothing is discarded if one fails
      const { urls, failed } = await uploaderRef.current.uploadAll();
      if (failed > 0) {
        toast.error(`${failed} photo(s) failed to upload. Check the message under each photo and press Submit again.`);
        return;
      }
      const finalImages = [...(formData.existingImages || []), ...urls];

      // Upload agent profile image if provided
      let agentProfileImageUrl = formData.existingAgentProfileImage || '';
      if (formData.agentProfileFile) {
        agentProfileImageUrl = await uploadLogo(formData.agentProfileFile);
      }

      const listingData = {
        title: formData.title,
        price: parseFloat(formData.price),
        type: formData.type,
        propertyType: formData.propertyType,
        propertyAddress: formData.propertyAddress,
        companyName: formData.companyName,
        workingAddress: formData.workingAddress,
        contactNumber: formData.contactNumber,
        whatsappNumber: formData.whatsappNumber,
        description: formData.description,
        images: finalImages,
        agentProfileImage: agentProfileImageUrl,
        ratesAndTaxes: parseFloat(formData.ratesAndTaxes) || 0,
        levies: parseFloat(formData.levies) || 0,
        specifications: {
          beds: parseInt(formData.specifications.beds) || 0,
          baths: parseInt(formData.specifications.baths) || 0,
          lounges: parseInt(formData.specifications.lounges) || 0,
          kitchens: parseInt(formData.specifications.kitchens) || 0,
          diningRooms: parseInt(formData.specifications.diningRooms) || 0,
          propertySize: parseFloat(formData.specifications.propertySize) || 0,
          floorSize: parseFloat(formData.specifications.floorSize) || 0,
          ratesTaxes: parseFloat(formData.specifications.ratesTaxes) || 0,
          hectares: parseFloat(formData.specifications.hectares) || 0
        },
        amenities: formData.amenities,
        customAmenities: formData.customAmenities,
        ownerId: currentUserId,
        ownerMode: appMode
      };

      if (editListing) {
        // Update existing listing
        await axios.put(`${API}/listings/${editListing.id}`, listingData);
        toast.success('Listing updated successfully!');
      } else {
        // Create new listing
        await axios.post(`${API}/listings`, listingData);
        toast.success('Listing submitted successfully!');
      }
      
      navigate('/dashboard');
    } catch (error) {
      console.error('Error submitting listing:', error);
      toast.error(`Failed to ${editListing ? 'update' : 'submit'} listing`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 py-8">
      {/* Subscription Status Banner */}
      {stats && stats.subscriptionStatus === 'none' && (
        <Card className="mb-6 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-purple-700 mb-1">
                  <i className="fas fa-gift mr-2"></i> Start Your 1-Month Free Trial!
                </h3>
                <p className="text-sm text-gray-700">Subscribe to list properties and enjoy 1 month free access.</p>
              </div>
              <Button 
                onClick={() => navigate('/subscription')}
                className="bg-purple-600 hover:bg-purple-700"
              >
                View Plans
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {stats && (stats.subscriptionStatus === 'active' || stats.subscriptionStatus === 'trial') && (
        <Card className="mb-6 bg-emerald-50 border-emerald-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-4">
                <span className="text-emerald-700 font-semibold">
                  <i className="fas fa-check-circle mr-1"></i> 
                  {stats.isTrial ? 'Free Trial Active' : 'Subscription Active'}
                </span>
                <span className="text-gray-600">
                  Properties: {stats.propertiesListed} / {stats.propertyLimit}
                </span>
              </div>
              {!stats.canListMore && !editListing && (
                <span className="text-red-600 font-semibold">
                  <i className="fas fa-exclamation-triangle mr-1"></i> Limit Reached
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-2xl" style={{ fontFamily: 'Playfair Display, serif' }}>
            {editListing ? 'Edit Listing' : 'Submit New Listing'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title">Listing Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="e.g., Sea Point Apartment"
                  required
                  data-testid="listing-title-input"
                />
              </div>
              
              <div>
                <Label htmlFor="price">Price (ZAR) *</Label>
                <Input
                  id="price"
                  type="number"
                  value={formData.price}
                  onChange={(e) => handleInputChange('price', e.target.value)}
                  placeholder="1500000"
                  required
                  data-testid="listing-price-input"
                />
              </div>
              
              <div>
                <Label htmlFor="type">Listing Type</Label>
                <Select value={formData.type} onValueChange={(value) => handleInputChange('type', value)}>
                  <SelectTrigger id="type" data-testid="listing-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sale">Sale</SelectItem>
                    <SelectItem value="rental">Rental</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="propertyType">Property Type</Label>
                <Select value={formData.propertyType} onValueChange={(value) => handleInputChange('propertyType', value)}>
                  <SelectTrigger id="propertyType" data-testid="property-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="apartment">Apartment</SelectItem>
                    <SelectItem value="bachelor_rooms">Bachelor Rooms</SelectItem>
                    <SelectItem value="house">House</SelectItem>
                    <SelectItem value="farm">Farm</SelectItem>
                    <SelectItem value="new_development">New Development</SelectItem>
                    <SelectItem value="cottage">Cottage</SelectItem>
                    <SelectItem value="backroom">Backroom</SelectItem>
                    <SelectItem value="vacant_land">Vacant Land</SelectItem>
                    <SelectItem value="office_space">Office Space</SelectItem>
                    <SelectItem value="commercial_property">Commercial Property</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="propertyAddress">Property Address *</Label>
                <Input
                  id="propertyAddress"
                  value={formData.propertyAddress}
                  onChange={(e) => handleInputChange('propertyAddress', e.target.value)}
                  placeholder="123 Main Street, Cape Town"
                  data-testid="property-address-input"
                />
              </div>
              
              <div>
                <Label htmlFor="companyName">Company/Agent Name</Label>
                <Input
                  id="companyName"
                  value={formData.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                  placeholder="Your company or agent name"
                />
              </div>
              
              <div>
                <Label htmlFor="workingAddress">Working Address</Label>
                <Input
                  id="workingAddress"
                  value={formData.workingAddress}
                  onChange={(e) => handleInputChange('workingAddress', e.target.value)}
                  placeholder="123 Business Street, Cape Town"
                />
              </div>
              
              <div>
                <Label htmlFor="contactNumber">Contact Number</Label>
                <Input
                  id="contactNumber"
                  type="tel"
                  value={formData.contactNumber}
                  onChange={(e) => handleInputChange('contactNumber', e.target.value)}
                  placeholder="+27 12 345 6789"
                />
              </div>
            </div>

            <Card className="bg-gray-50">
              <CardHeader>
                <CardTitle className="text-lg">Property Specifications</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 mb-4">
                  <Input type="number" placeholder="Bedrooms" value={formData.specifications.beds} onChange={(e) => handleSpecChange('beds', e.target.value)} data-testid="beds-input" />
                  <Input type="number" placeholder="Bathrooms" value={formData.specifications.baths} onChange={(e) => handleSpecChange('baths', e.target.value)} data-testid="baths-input" />
                  <Input type="number" placeholder="Lounges" value={formData.specifications.lounges} onChange={(e) => handleSpecChange('lounges', e.target.value)} />
                  <Input type="number" placeholder="Kitchens" value={formData.specifications.kitchens} onChange={(e) => handleSpecChange('kitchens', e.target.value)} />
                  <Input type="number" placeholder="Dining Rooms" value={formData.specifications.diningRooms} onChange={(e) => handleSpecChange('diningRooms', e.target.value)} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Input type="number" placeholder="Property Size (sqm)" value={formData.specifications.propertySize} onChange={(e) => handleSpecChange('propertySize', e.target.value)} />
                  <Input type="number" placeholder="Floor Size (sqm)" value={formData.specifications.floorSize} onChange={(e) => handleSpecChange('floorSize', e.target.value)} />
                </div>
                
                {/* Monthly Costs */}
                <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <Label className="text-blue-800 font-semibold flex items-center gap-2 mb-3">
                    <i className="fas fa-file-invoice-dollar"></i>
                    Monthly Costs (Optional)
                  </Label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="ratesAndTaxes" className="text-xs text-blue-700">Rates & Taxes (R/month)</Label>
                      <Input 
                        id="ratesAndTaxes"
                        type="number" 
                        placeholder="e.g., 1500" 
                        value={formData.ratesAndTaxes} 
                        onChange={(e) => handleInputChange('ratesAndTaxes', e.target.value)}
                        className="border-blue-300"
                        data-testid="rates-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="levies" className="text-xs text-blue-700">Levies (R/month)</Label>
                      <Input 
                        id="levies"
                        type="number" 
                        placeholder="e.g., 2500" 
                        value={formData.levies} 
                        onChange={(e) => handleInputChange('levies', e.target.value)}
                        className="border-blue-300"
                        data-testid="levies-input"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">These help buyers understand the true cost of ownership</p>
                </div>
                
                {/* Land Size - Only shown for Vacant Land or Farm */}
                {(formData.propertyType === 'vacant_land' || formData.propertyType === 'farm') && (
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <Label htmlFor="landSizeHectares" className="text-amber-800 font-semibold flex items-center gap-2">
                      <i className="fas fa-expand-arrows-alt"></i>
                      Land Size (ha) *
                    </Label>
                    <p className="text-xs text-amber-600 mb-2">Required for {formData.propertyType === 'farm' ? 'Farm' : 'Vacant Land'} properties</p>
                    <Input
                      id="landSizeHectares"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="e.g., 10.5"
                      value={formData.specifications.hectares}
                      onChange={(e) => handleSpecChange('hectares', e.target.value)}
                      className="border-amber-300 focus:border-amber-500"
                      data-testid="land-size-hectares-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Enter the total land size in hectares (1 hectare = 10,000 m²)</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="bg-gray-50">
              <CardHeader>
                <CardTitle className="text-lg">Amenities & Features</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Predefined Amenities */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-6">
                  <div className="flex items-center space-x-2">
                    <Checkbox id="pool" checked={formData.amenities.pool} onCheckedChange={(checked) => handleAmenityChange('pool', checked)} />
                    <label htmlFor="pool" className="text-sm cursor-pointer">
                      <i className="fas fa-swimming-pool text-blue-500 mr-2"></i>Swimming Pool
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="garden" checked={formData.amenities.garden} onCheckedChange={(checked) => handleAmenityChange('garden', checked)} />
                    <label htmlFor="garden" className="text-sm cursor-pointer">
                      <i className="fas fa-leaf text-green-500 mr-2"></i>Garden
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="security" checked={formData.amenities.security} onCheckedChange={(checked) => handleAmenityChange('security', checked)} />
                    <label htmlFor="security" className="text-sm cursor-pointer">
                      <i className="fas fa-shield-alt text-gray-600 mr-2"></i>Security
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="garage" checked={formData.amenities.garage} onCheckedChange={(checked) => handleAmenityChange('garage', checked)} />
                    <label htmlFor="garage" className="text-sm cursor-pointer">
                      <i className="fas fa-car text-gray-500 mr-2"></i>Garage
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="airConditioning" checked={formData.amenities.airConditioning} onCheckedChange={(checked) => handleAmenityChange('airConditioning', checked)} />
                    <label htmlFor="airConditioning" className="text-sm cursor-pointer">
                      <i className="fas fa-snowflake text-cyan-500 mr-2"></i>Air Conditioning
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="petFriendly" checked={formData.amenities.petFriendly} onCheckedChange={(checked) => handleAmenityChange('petFriendly', checked)} />
                    <label htmlFor="petFriendly" className="text-sm cursor-pointer">
                      <i className="fas fa-paw text-yellow-700 mr-2"></i>Pet Friendly
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="fibre" checked={formData.amenities.fibre} onCheckedChange={(checked) => handleAmenityChange('fibre', checked)} />
                    <label htmlFor="fibre" className="text-sm cursor-pointer">
                      <i className="fas fa-wifi text-indigo-500 mr-2"></i>Fibre Internet
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="borehole" checked={formData.amenities.borehole} onCheckedChange={(checked) => handleAmenityChange('borehole', checked)} />
                    <label htmlFor="borehole" className="text-sm cursor-pointer">
                      <i className="fas fa-tint text-blue-600 mr-2"></i>Borehole
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="solarPanels" checked={formData.amenities.solarPanels} onCheckedChange={(checked) => handleAmenityChange('solarPanels', checked)} />
                    <label htmlFor="solarPanels" className="text-sm cursor-pointer">
                      <i className="fas fa-solar-panel text-yellow-500 mr-2"></i>Solar Panels
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox id="staffAccommodation" checked={formData.amenities.staffAccommodation} onCheckedChange={(checked) => handleAmenityChange('staffAccommodation', checked)} />
                    <label htmlFor="staffAccommodation" className="text-sm cursor-pointer">
                      <i className="fas fa-home text-purple-500 mr-2"></i>Staff Quarters
                    </label>
                  </div>
                </div>
                
                {/* Custom Amenities */}
                <div className="border-t pt-4">
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    Add Custom Amenities
                  </Label>
                  <div className="flex gap-2 mb-3">
                    <Input
                      type="text"
                      placeholder="e.g., Built-in braai, Wine cellar..."
                      value={customAmenityInput}
                      onChange={(e) => setCustomAmenityInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          if (customAmenityInput.trim()) {
                            setFormData(prev => ({
                              ...prev,
                              customAmenities: [...prev.customAmenities, customAmenityInput.trim()]
                            }));
                            setCustomAmenityInput('');
                          }
                        }
                      }}
                      className="flex-1"
                      data-testid="custom-amenity-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        if (customAmenityInput.trim()) {
                          setFormData(prev => ({
                            ...prev,
                            customAmenities: [...prev.customAmenities, customAmenityInput.trim()]
                          }));
                          setCustomAmenityInput('');
                        }
                      }}
                      data-testid="add-custom-amenity-btn"
                    >
                      <i className="fas fa-plus mr-1"></i>Add
                    </Button>
                  </div>
                  
                  {/* Display Custom Amenities */}
                  {formData.customAmenities.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {formData.customAmenities.map((amenity, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-50 text-emerald-700 rounded-full text-sm"
                        >
                          <i className="fas fa-check-circle text-emerald-500 text-xs"></i>
                          {amenity}
                          <button
                            type="button"
                            onClick={() => {
                              setFormData(prev => ({
                                ...prev,
                                customAmenities: prev.customAmenities.filter((_, i) => i !== index)
                              }));
                            }}
                            className="ml-1 text-emerald-400 hover:text-red-500 transition-colors"
                          >
                            <i className="fas fa-times text-xs"></i>
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Agent Profile Image Upload */}
            <Card className="bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <i className="fas fa-user-tie text-emerald-600"></i>
                  Agent Profile Image
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">
                  Upload your profile photo to appear with this listing. Agents with profile photos get a verified badge.
                </p>
                <div className="flex items-center gap-4">
                  {/* Profile Preview */}
                  <div className="relative">
                    <div className="w-20 h-20 rounded-full bg-white border-4 border-emerald-200 flex items-center justify-center overflow-hidden">
                      {agentProfilePreview ? (
                        <img 
                          src={agentProfilePreview} 
                          alt="Profile preview" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <i className="fas fa-user text-3xl text-gray-300"></i>
                      )}
                    </div>
                    {agentProfilePreview && (
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                        <i className="fas fa-check text-white text-xs"></i>
                      </div>
                    )}
                  </div>
                  
                  {/* Upload Controls */}
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        if (file.size > 5 * 1024 * 1024) {
                          toast.error('Image must be less than 5MB');
                          return;
                        }
                        try {
                          // Compress before preview/storage to keep payload small
                          const compressed = await compressImage(file, 512, 0.82);
                          setAgentProfilePreview(compressed);
                          setFormData(prev => ({ ...prev, agentProfileFile: file }));
                        } catch (err) {
                          console.error('Agent profile compression failed', err);
                          toast.error('Could not process image');
                        }
                      }}
                      className="hidden"
                      id="agent-profile-upload"
                      data-testid="agent-profile-upload"
                    />
                    <label
                      htmlFor="agent-profile-upload"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-emerald-300 rounded-lg cursor-pointer hover:bg-emerald-50 transition-colors text-sm font-medium text-emerald-700"
                    >
                      <i className="fas fa-camera"></i>
                      {agentProfilePreview ? 'Change Photo' : 'Upload Photo'}
                    </label>
                    {agentProfilePreview && (
                      <button
                        type="button"
                        onClick={() => {
                          setAgentProfilePreview('');
                          setFormData(prev => ({ ...prev, agentProfileFile: null, existingAgentProfileImage: '' }));
                        }}
                        className="ml-2 text-sm text-red-500 hover:text-red-600"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Describe your property..."
                rows={4}
                data-testid="listing-description-input"
              />
            </div>

            {/* Property Images - Using Professional Image Uploader */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <Label className="text-lg font-semibold mb-3 flex items-center gap-2">
                <i className="fas fa-camera text-emerald-600"></i>
                Property Images
              </Label>
              <ImageUploader
                ref={uploaderRef}
                existingCount={(formData.existingImages || []).length}
                ownerId={currentUserId}
                maxImages={20}
                disabled={uploading}
              />
              
              {/* Show existing images */}
              {formData.existingImages && formData.existingImages.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">
                    Current Images ({formData.existingImages.length})
                  </p>
                  <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
                    {formData.existingImages.map((img, idx) => (
                      <div key={idx} className="relative aspect-video rounded overflow-hidden">
                        <img 
                          src={typeof img === 'string' ? img : img.thumbnail || img.full}
                          alt={`Property ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newImages = formData.existingImages.filter((_, i) => i !== idx);
                            handleInputChange('existingImages', newImages);
                          }}
                          className="absolute top-0 right-0 bg-red-500 text-white w-5 h-5 flex items-center justify-center text-xs rounded-bl"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="flex gap-4 pt-2">
              <Button type="submit" disabled={uploading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="submit-listing-btn">
                {uploading ? 'Uploading photos...' : (editListing ? 'Update Listing' : 'Submit Listing')}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate('/dashboard')}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </main>
  );
};

export default NewListing;